import { useMemo } from 'react'
import { useIntl } from 'react-intl'

interface LabelValueOption {
  label: string
  value: string
}

function useReviewLabels() {
  const intl = useIntl()

  return useMemo(() => {
    const HOTEL_DESCRIBE_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: 'C6c2AisU4i',
          defaultMessage: 'Relaxing',
          description: 'Hotel review describe option label for relaxing in the user reviews section',
        }),
        value: 'relaxing',
      },
      {
        label: intl.formatMessage({
          id: 'qYRxYx+X45',
          defaultMessage: 'Comfortable',
          description: 'Hotel review describe option label for comfortable',
        }),
        value: 'comfortable',
      },
      {
        label: intl.formatMessage({
          id: 'SlPCO+XI8z',
          defaultMessage: 'Clean',
          description: 'Hotel review describe option label for clean in the reviews section',
        }),
        value: 'clean',
      },
      {
        label: intl.formatMessage({
          id: 'nja/5SXuOK',
          defaultMessage: 'Value for money',
          description: 'Hotel review describe option label for value for money',
        }),
        value: 'value_for_money',
      },
      {
        label: intl.formatMessage({
          id: 'L2cqjyd8ZL',
          defaultMessage: 'Good facilities',
          description: 'Hotel review describe option label for good facilities',
        }),
        value: 'good_facilities',
      },
      {
        label: intl.formatMessage({
          id: 'uCSEsTy7jN',
          defaultMessage: 'Kid friendly',
          description: 'Hotel review describe option label for kid friendly',
        }),
        value: 'kid_friendly',
      },
    ]

    const EXPERIENCE_DESCRIBE_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: '6ZR3lD2yEt',
          defaultMessage: 'Relaxing',
          description: 'Experience review describe option label for relaxing',
        }),
        value: 'relaxing',
      },
      {
        label: intl.formatMessage({
          id: 'f6LqnwDWg9',
          defaultMessage: 'Comfortable',
          description: 'Experience review describe option label for comfortable',
        }),
        value: 'comfortable',
      },
      {
        label: intl.formatMessage({
          id: '4VhEUTbQlw',
          defaultMessage: 'Clean',
          description: 'Experience review describe option label for clean in the user reviews section',
        }),
        value: 'clean',
      },
      {
        label: intl.formatMessage({
          id: 'HzDtamoPLU',
          defaultMessage: 'Variety of meals',
          description: 'Experience review describe option label for variety of meals',
        }),
        value: 'variety_of_meals',
      },
      {
        label: intl.formatMessage({
          id: 'F06CFXivwf',
          defaultMessage: 'Good value',
          description: 'Experience review describe option label for good value',
        }),
        value: 'good_value',
      },
      {
        label: intl.formatMessage({
          id: 'NAbxuCd/Ey',
          defaultMessage: 'On-time',
          description: 'Experience review describe option label for on-time',
        }),
        value: 'on_time',
      },
    ]

    const TOUR_DESCRIBE_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: 'j1woZfAw/g',
          defaultMessage: 'Culturally immersive',
          description: 'Tour review describe option label for culturally immersive',
        }),
        value: 'culturally_immersive',
      },
      {
        label: intl.formatMessage({
          id: 'N1EW9pqNq4',
          defaultMessage: 'Comfortable & convenient',
          description: 'Tour review describe option label for comfortable and convenient',
        }),
        value: 'comfortable_convenient',
      },
      {
        label: intl.formatMessage({
          id: '5NI4OJIeYz',
          defaultMessage: 'Engaging itinerary',
          description: 'Tour review describe option label for engaging itinerary',
        }),
        value: 'engaging_itinerary',
      },
      {
        label: intl.formatMessage({
          id: 's8cF3SAmV4',
          defaultMessage: 'Expert guides',
          description: 'Tour review describe option label for expert guides',
        }),
        value: 'expert_guides',
      },
      {
        label: intl.formatMessage({
          id: 'kEFjQO52Yu',
          defaultMessage: 'Flexible options',
          description: 'Tour review describe option label for flexible options',
        }),
        value: 'flexible_options',
      },
      {
        label: intl.formatMessage({
          id: 'LyJbZkvYXH',
          defaultMessage: 'Hidden gems',
          description: 'Tour review describe option label for hidden gems in the user reviews section',
        }),
        value: 'hidden_gems',
      },
      {
        label: intl.formatMessage({
          id: '3m3b+FnHXF',
          defaultMessage: 'Iconic landmarks',
          description: 'Tour review describe option label for iconic landmarks',
        }),
        value: 'iconic_landmarks',
      },
      {
        label: intl.formatMessage({
          id: 'icXP99RLRa',
          defaultMessage: 'Good value',
          description: 'Tour review describe option label for good value in the user reviews section',
        }),
        value: 'good_value',
      },
      {
        label: intl.formatMessage({
          id: '2ofII9O3JI',
          defaultMessage: 'Luxurious',
          description: 'Tour review describe option label for luxurious in the user reviews section',
        }),
        value: 'luxurious',
      },
      {
        label: intl.formatMessage({
          id: 'VxUHQeP6hg',
          defaultMessage: 'Memorable',
          description: 'Tour review describe option label for memorable in the user reviews section',
        }),
        value: 'memorable',
      },
      {
        label: intl.formatMessage({
          id: '/3UbngkK69',
          defaultMessage: 'Scenic beauty',
          description: 'Tour review describe option label for scenic beauty',
        }),
        value: 'scenic',
      },
      {
        label: intl.formatMessage({
          id: 'dLcbdjRMf8',
          defaultMessage: 'Safe & secure',
          description: 'Tour review describe option label for safe and secure',
        }),
        value: 'safe_secure',
      },
    ]

    const COMPANIONSHIP_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: 'rRi6xVjSpI',
          defaultMessage: 'Just myself',
          description: 'Companionship option label for solo travel in the user reviews section',
        }),
        value: 'alone',
      },
      {
        label: intl.formatMessage({
          id: 'QKAp2IpBCi',
          defaultMessage: 'Partner',
          description: 'Companionship option label for travelling with a partner',
        }),
        value: 'partner',
      },
      {
        label: intl.formatMessage({
          id: 'Ryi2w01Xk5',
          defaultMessage: 'Family',
          description: 'Companionship option label for family travel in the user reviews section',
        }),
        value: 'family',
      },
      {
        label: intl.formatMessage({
          id: 'qHUXDj2j81',
          defaultMessage: 'Friends',
          description: 'Companionship option label for travelling with friends',
        }),
        value: 'friends',
      },
      {
        label: intl.formatMessage({
          id: 'Bt208m8gel',
          defaultMessage: 'Other',
          description: 'Companionship option label for other travel companions',
        }),
        value: 'other',
      },
    ]

    const COMPANIONSHIP_ENUM: Record<string, string> = {
      alone: intl.formatMessage({
        id: 'w44SxB7XLz',
        defaultMessage: 'Just myself',
        description: 'Companionship display label for solo travel on review tiles',
      }),
      partner: intl.formatMessage({
        id: 'pzPa2uUo+7',
        defaultMessage: 'Partner',
        description: 'Companionship display label for partner travel on review tiles',
      }),
      family: intl.formatMessage({
        id: 'f72hg6PRgf',
        defaultMessage: 'Family',
        description: 'Companionship display label for family travel on review tiles',
      }),
      friends: intl.formatMessage({
        id: 'V00LbY+3I/',
        defaultMessage: 'Friends',
        description: 'Companionship display label for friends travel on review tiles',
      }),
      other: intl.formatMessage({
        id: 'A5uyi7t3T2',
        defaultMessage: 'Other',
        description: 'Companionship display label for other travel companions on review tiles',
      }),
    }

    const DEFINITION_ENUM: Record<string, string> = {
      relaxing: intl.formatMessage({
        id: 'ParncI34gQ',
        defaultMessage: 'Relaxing',
        description: 'Review definition tag label for relaxing in the reviews section',
      }),
      comfortable: intl.formatMessage({
        id: 'PC7LgsZCmg',
        defaultMessage: 'Comfortable',
        description: 'Review definition tag label for comfortable in the reviews section',
      }),
      clean: intl.formatMessage({
        id: 'FReBOf1oT/',
        defaultMessage: 'Clean',
        description: 'Review definition tag label for clean in the guest reviews section',
      }),
      value_for_money: intl.formatMessage({
        id: 'bKjhaDJ5ts',
        defaultMessage: 'Value for money',
        description: 'Review definition tag label for value for money in the user reviews section',
      }),
      good_facilities: intl.formatMessage({
        id: 'HuSp5TEwvS',
        defaultMessage: 'Good facilities',
        description: 'Review definition tag label for good facilities in the user reviews section',
      }),
      kid_friendly: intl.formatMessage({
        id: 'Yle9f+uexy',
        defaultMessage: 'Kid friendly',
        description: 'Review definition tag label for kid friendly in the reviews section',
      }),
      variety_of_meals: intl.formatMessage({
        id: 'WhCcEbZRzc',
        defaultMessage: 'Variety of meals',
        description: 'Review definition tag label for variety of meals in the user reviews section',
      }),
      good_value: intl.formatMessage({
        id: '7onm2uBraX',
        defaultMessage: 'Good value',
        description: 'Review definition tag label for good value in the reviews section',
      }),
      on_time: intl.formatMessage({
        id: 'OiSckaW6ZS',
        defaultMessage: 'On-time',
        description: 'Review definition tag label for on-time in the guest reviews section',
      }),
      expert_guides: intl.formatMessage({
        id: '8Jn6eqHMRR',
        defaultMessage: 'Expert guides',
        description: 'Review definition tag label for expert guides in the user reviews section',
      }),
      scenic: intl.formatMessage({
        id: 'Y9VAohmkMI',
        defaultMessage: 'Scenic beauty',
        description: 'Review definition tag label for scenic beauty in the user reviews section',
      }),
      immersive: intl.formatMessage({
        id: 'efsOyLKx8X',
        defaultMessage: 'Immersive',
        description: 'Review definition tag label for immersive in the reviews section',
      }),
      luxurious: intl.formatMessage({
        id: 'wxIaxMmSFa',
        defaultMessage: 'Luxurious',
        description: 'Review definition tag label for luxurious in the reviews section',
      }),
      variety_of_activities: intl.formatMessage({
        id: 'EkReqW8zLc',
        defaultMessage: 'Variety of activities',
        description: 'Review definition tag label for variety of activities',
      }),
      culturally_immersive: intl.formatMessage({
        id: 'ywW91mzjfY',
        defaultMessage: 'Culturally immersive',
        description: 'Review definition tag label for culturally immersive',
      }),
      comfortable_convenient: intl.formatMessage({
        id: 'p/+KqL9PLJ',
        defaultMessage: 'Comfortable & convenient',
        description: 'Review definition tag label for comfortable and convenient',
      }),
      engaging_itinerary: intl.formatMessage({
        id: 'LoXFB2o7n2',
        defaultMessage: 'Engaging itinerary',
        description: 'Review definition tag label for engaging itinerary',
      }),
      flexible_options: intl.formatMessage({
        id: '0k6z7P6F/C',
        defaultMessage: 'Flexible options',
        description: 'Review definition tag label for flexible options in the user reviews section',
      }),
      hidden_gems: intl.formatMessage({
        id: 'rIgHEX5fPy',
        defaultMessage: 'Hidden gems',
        description: 'Review definition tag label for hidden gems in the reviews section',
      }),
      iconic_landmarks: intl.formatMessage({
        id: 'NNWORj+fJl',
        defaultMessage: 'Iconic landmarks',
        description: 'Review definition tag label for iconic landmarks in the user reviews section',
      }),
      memorable: intl.formatMessage({
        id: 'A/+JfU0AFf',
        defaultMessage: 'Memorable',
        description: 'Review definition tag label for memorable in the reviews section',
      }),
      safe_secure: intl.formatMessage({
        id: 'ixSlO26FyY',
        defaultMessage: 'Safe & secure',
        description: 'Review definition tag label for safe and secure in the user reviews section',
      }),
    }

    const SORT_OPTION_NEWEST: LabelValueOption = {
      label: intl.formatMessage({
        id: 'PEVEv38cEH',
        defaultMessage: 'Most recent',
        description: 'Review sort option label for most recent reviews in the user reviews section',
      }),
      value: 'newest',
    }

    const SORT_OPTION_OLDEST: LabelValueOption = {
      label: intl.formatMessage({
        id: '47rR9pdxgK',
        defaultMessage: 'Oldest',
        description: 'Review sort option label for oldest reviews in the reviews section',
      }),
      value: 'oldest',
    }

    const SORT_OPTION_BEST_RATING: LabelValueOption = {
      label: intl.formatMessage({
        id: 'sLxLlkNMVz',
        defaultMessage: 'Highest rating',
        description: 'Review sort option label for highest rated reviews',
      }),
      value: 'best_rating',
    }

    const SORT_OPTION_WORST_RATING: LabelValueOption = {
      label: intl.formatMessage({
        id: 'ZllPY9DeCO',
        defaultMessage: 'Lowest rating',
        description: 'Review sort option label for lowest rated reviews in the user reviews section',
      }),
      value: 'worst_rating',
    }

    const SORT_OPTION_MOST_RELEVANT: LabelValueOption = {
      label: intl.formatMessage({
        id: 'bcWagswgT4',
        defaultMessage: 'Most relevant',
        description: 'Review sort option label for most relevant reviews',
      }),
      value: 'most_relevant',
    }

    const userReviewsSortOptions: Array<LabelValueOption> = [
      SORT_OPTION_MOST_RELEVANT,
      SORT_OPTION_NEWEST,
      SORT_OPTION_OLDEST,
      SORT_OPTION_BEST_RATING,
      SORT_OPTION_WORST_RATING,
    ]

    const REVIEW_PANEL_SORT_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: 'kTKMlRlGh0',
          defaultMessage: 'Most recent',
          description: 'Review panel sort option for most recent reviews in the user reviews section',
        }),
        value: 'newest',
      },
      {
        label: intl.formatMessage({
          id: '7vCAWYMnmb',
          defaultMessage: 'Highest rated',
          description: 'Review panel sort option for highest rated reviews',
        }),
        value: 'best_rating',
      },
      {
        label: intl.formatMessage({
          id: 'wctOZaI9GR',
          defaultMessage: 'Lowest rated',
          description: 'Review panel sort option for lowest rated reviews in the user reviews section',
        }),
        value: 'worst_rating',
      },
      {
        label: intl.formatMessage({
          id: 'huKfCm8v6W',
          defaultMessage: 'Recommended',
          description: 'Review panel sort option for recommended reviews in the user reviews section',
        }),
        value: 'recommend',
      },
    ]

    const REVIEW_PANEL_RATING_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: 'Q6iXxLsONc',
          defaultMessage: 'All',
          description: 'Review panel rating filter option for all ratings in the user reviews section',
        }),
        value: '',
      },
      {
        label: intl.formatMessage({
          id: 'TxjymdKJRl',
          defaultMessage: 'Exceptional (9-10)',
          description: 'Review panel rating filter option for exceptional ratings',
        }),
        value: 'exceptional',
      },
      {
        label: intl.formatMessage({
          id: '7V2jRS9x1x',
          defaultMessage: 'Good (7-8)',
          description: 'Review panel rating filter option for good ratings',
        }),
        value: 'good',
      },
      {
        label: intl.formatMessage({
          id: 'VTOuKlETAV',
          defaultMessage: 'Okay (5-6)',
          description: 'Review panel rating filter option for okay ratings',
        }),
        value: 'okay',
      },
      {
        label: intl.formatMessage({
          id: 'MFG9OLyMby',
          defaultMessage: 'Poor (3-4)',
          description: 'Review panel rating filter option for poor ratings',
        }),
        value: 'poor',
      },
      {
        label: intl.formatMessage({
          id: 'jsqGBo/EaO',
          defaultMessage: 'Terrible (1-2)',
          description: 'Review panel rating filter option for terrible ratings',
        }),
        value: 'very-poor',
      },
    ]

    const REVIEW_PANEL_TRAVELLER_OPTIONS: Array<LabelValueOption> = [
      {
        label: intl.formatMessage({
          id: '/2DuFNIP9t',
          defaultMessage: 'All',
          description: 'Review panel traveller filter option for all traveller types',
        }),
        value: '',
      },
      {
        label: intl.formatMessage({
          id: 'MeAy7karfe',
          defaultMessage: 'Partner',
          description: 'Review panel traveller filter option for partner travel',
        }),
        value: 'partner',
      },
      {
        label: intl.formatMessage({
          id: 'Nz/0QxgieA',
          defaultMessage: 'Family',
          description: 'Review panel traveller filter option for family travel',
        }),
        value: 'family',
      },
      {
        label: intl.formatMessage({
          id: 'boFxL3Dl0C',
          defaultMessage: 'Friends',
          description: 'Review panel traveller filter option for friends travel',
        }),
        value: 'friends',
      },
      {
        label: intl.formatMessage({
          id: 'Mqz7mB3ind',
          defaultMessage: 'Just myself',
          description: 'Review panel traveller filter option for solo travel',
        }),
        value: 'alone',
      },
    ]

    return {
      HOTEL_DESCRIBE_OPTIONS,
      EXPERIENCE_DESCRIBE_OPTIONS,
      TOUR_DESCRIBE_OPTIONS,
      COMPANIONSHIP_OPTIONS,
      COMPANIONSHIP_ENUM,
      DEFINITION_ENUM,
      userReviewsSortOptions,
      SORT_OPTION_MOST_RELEVANT,
      REVIEW_PANEL_SORT_OPTIONS,
      REVIEW_PANEL_RATING_OPTIONS,
      REVIEW_PANEL_TRAVELLER_OPTIONS,
    }
  }, [intl])
}

export default useReviewLabels
